// ServiceStaffModal.jsx
import React, { useState, useEffect } from "react";
import CrossIcon from "../../../../assets/icons/maid-module-icons/CrossIcon.svg";
import BlueArrow from "../../../../assets/icons/maid-module-icons/BlueArrow.svg";
import DigitalPassGenerator from "./DigitalPassGenerator";
import BuildingIcon from "../../../../assets/icons/maid-module-icons/buildingsIcon.svg";
import AadhaarCard from "../../../../assets/img/aadhaarCard.svg";
import UserIcon from "../../../../assets/icons/maid-module-icons/UserIcon.svg";
import MobileIcon from "../../../../assets/icons/maid-module-icons/MobileIcon.svg";
import ImagePreviewModal from "../../modals/modals/ImagePreviewModal";
import { commonAPI } from "../../../../services/CommonAPIService";
import { toast, ToastContainer } from "react-toastify";

// ── Helpers ──────────────────────────────────────────────────

const parseMaskedSetting = (value) =>
  value === true || value === 1 || value === "1" || value === "true" ||
  String(value).toLowerCase() === "true";

const getDocType = (docTypeStr) => {
  const s = (docTypeStr || "").toLowerCase();
  if (s.includes("aadhaar") || s.includes("aadhar")) return "aadhaar";
  if (s.includes("pan"))                              return "pan";
  if (s.includes("voter") || s.includes("epic"))     return "voter";
  return "other";
};

const MASKED_PREFIX = {
  aadhaar : "**** **** ",
  pan     : "****** ",
  voter   : "****** ",
  other   : "****** ",
};

const formatDocNumber = (docNumber, docType, masked) => {
  if (!docNumber) return "Not provided";
  const str = String(docNumber);
  if (!masked) return str;
  return MASKED_PREFIX[docType] + str.slice(-4);
};

// ─────────────────────────────────────────────────────────────

const ServiceStaffModal = ({ show, onHide, staffData, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [showPassGenerator, setShowPassGenerator] = useState(false);
  const [createdStaff, setCreatedStaff] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  // ── Masked setting ───────────────────────────────────────
  const [showDocMasked, setShowDocMasked] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    const loadSetting = async () => {
      const auth = localStorage.getItem("userData");
      if (!auth) { setSettingsLoading(false); return; }

      let userData;
      try { userData = JSON.parse(auth); } catch {
        setSettingsLoading(false);
        return;
      }

      try {
        const response = await commonAPI("AppSettings", undefined, {
          urlVariable: null,
          buildingId: userData?.buildingId || null,
        });

        if (response?.success === true && Array.isArray(response.data)) {
          const setting = response.data.find(
            (item) => item.key === "SHOW_IDPROOF_MASKED"
          );
          setShowDocMasked(parseMaskedSetting(setting?.value));
        } else {
          setShowDocMasked(false);
        }
      } catch (err) {
        console.error("AppSettings Error:", err);

        try {
          await commonAPI("ErrorLogMaster", {
            userId: userData?.userId,
            mobileNumber: userData?.mobileNumber,
            requestJSON: null,
            uri: "/api/Master/AppSettings",
            errorSource: "CuroNextPortal",
            errorNumber: "SRCSTF02",
            errorSummary: err?.message,
            errorDescription: err?.message,
            fileClassName: "ServiceStaffModal.js",
            functionName: "loadSetting",
          });
        } catch (logErr) {
          console.error("Error while logging error:", logErr);
        }

        setShowDocMasked(false);
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSetting();
  }, []);

  if (!show || !staffData) return null;

  const {
    name,
    mobileNumber,
    gender,
    vehicleType,
    vehicleNumber,
    flats,
    documentType,
    documentNumber,
    profileImage,
    documentFile,
    selectedDocumentId,
    selectedFlats,
    roleId,
    roleName = "Staff",
  } = staffData;

  const docType = getDocType(documentType);

  const handleSubmit = async () => {
    setLoading(true);

    let payload = null;
    const auth = JSON.parse(localStorage.getItem("userData") || "{}");

    try {
      const userId = auth?.userId || "0";

      const fileToBase64 = (file) =>
        new Promise((resolve, reject) => {
          if (!file) return resolve("");
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
        });

      const [userImageBase64, docBase64] = await Promise.all([
        profileImage ? fileToBase64(profileImage) : Promise.resolve(""),
        documentFile ? fileToBase64(documentFile) : Promise.resolve(""),
      ]);

      payload = {
        createdUserId: userId.toString(),
        userName: name.trim(),
        mobileNumber: mobileNumber || null,
        gender,
        roleId: roleId || 0,
        userImagebase64String: userImageBase64,
        lstDocuments: documentFile
          ? [{
              docId: Number(selectedDocumentId) || 0,
              docNumber: documentNumber?.toString() || "",
              docBase64,
            }]
          : [],
        lstFlats: selectedFlats.map((flat) => ({
          towerId: flat.towerId || 0,
          flatId: flat.flatId || 0,
        })),
        vehicleType:
          vehicleType === "2W" ? "2WHEELER" :
          vehicleType === "4W" ? "4WHEELER" : "",
        vehicleNumber: vehicleNumber || "",
      };

      const response = await commonAPI("ServiceStaffV1", payload, {
        buildingId: auth?.buildingId,
      });

      if (response?.success) {
        toast.success(`${roleName} added successfully!`);

        const serverData = {
          ...staffData,
          id: response?.data?.staffId || null,
          curoNextId: response?.data?.passCode || "Pending Approval",
          status: response?.data?.currentStatus || "Pending",
        };

        setCreatedStaff(serverData);
        setShowPassGenerator(true);
      } else {
        throw new Error(response?.message?.[0] || `Failed to add ${roleName}`);
      }

    } catch (err) {
      console.error("ServiceStaffV1 Error:", err);

      const bodyError = {
        userId: auth?.userId || null,
        mobileNumber: auth?.mobileNumber || null,
        requestJSON: JSON.stringify(payload),
        uri: "/ap/User/ServiceStaffV1",
        errorSource: "CuroNextPortal",
        errorNumber: "SRCSTF01",
        errorSummary: err?.message,
        errorDescription: JSON.stringify(err),
        fileClassName: "ServiceStaffModal.js",
        functionName: "handleSubmit",
      };

      try {
        await commonAPI("ErrorLogMaster", bodyError);
      } catch (logErr) {
        console.error("Error while logging error:", logErr);
      }

      toast.error(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openPreview = (file, title) => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setPreviewTitle(title);
      setShowModal(true);
    }
  };

  const handleClosePreview = () => {
    setShowModal(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setPreviewTitle("");
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />

      {/* Backdrop */}
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1080 }} />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1090,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="bg-white d-flex flex-column"
          style={{
            width: "50%",
            maxWidth: 600,
            height: "96vh",
            borderRadius: 18,
            overflow: "hidden",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {/* Header */}
          <div
            className="d-flex justify-content-between align-items-center px-4 py-3 text-white"
            style={{ backgroundColor: "#1C80D2" }}
          >
            <div className="d-flex align-items-center gap-2">
              <button
                onClick={onHide}
                className="d-flex align-items-center justify-content-center rounded-circle"
                style={{ width: 38, height: 38, backgroundColor: "#ffffff", border: "1px solid #e5e5e5" }}
              >
                <img src={BlueArrow} alt="back" style={{ width: 16, height: 16 }} />
              </button>
              <h5 className="mb-0">Service Staff Details</h5>
            </div>
            <button
              className="btn p-0 d-flex align-items-center justify-content-center bg-white"
              style={{ width: 24, height: 24, borderRadius: 6 }}
              onClick={onHide}
            >
              <img src={CrossIcon} alt="cross" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-grow-1 px-5 py-4" style={{ overflowY: "auto", display: "flex", flexDirection: "column" }}>

            {/* Role */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 8 }}>
                Service Staff
              </label>
              <div style={{ position: "relative" }}>
                <img
                  src={UserIcon}
                  alt="staff"
                  style={{
                    position: "absolute", left: 12, top: "50%",
                    transform: "translateY(-50%)", width: 15, height: 15, objectFit: "contain",
                  }}
                />
                <input
                  type="text"
                  value={roleName}
                  readOnly
                  style={{
                    width: "100%", padding: "10px 14px 10px 38px",
                    border: "1px solid #d1d5db", borderRadius: 8, fontSize: 15, background: "#f9fafb",
                  }}
                />
              </div>
            </div>

            {/* Mobile */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 8 }}>
                Mobile Number
              </label>
              <div style={{ position: "relative" }}>
                <img
                  src={MobileIcon}
                  alt="mobile"
                  style={{
                    position: "absolute", left: 12, top: "50%",
                    transform: "translateY(-50%)", width: 18, height: 18, objectFit: "contain",
                  }}
                />
                <input
                  type="text"
                  value={mobileNumber || "Not provided"}
                  readOnly
                  style={{
                    width: "100%", padding: "10px 14px 10px 38px",
                    border: "1px solid #d1d5db", borderRadius: 8, fontSize: 15, background: "#f9fafb",
                  }}
                />
              </div>
            </div>

            {/* Document */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 8 }}>
                Document Details
              </label>
              <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <img src={AadhaarCard} alt="Document" style={{ width: 35, height: "auto" }} />
                  <span style={{ fontSize: 14, color: "#374151" }}>{documentType || "Not selected"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  {documentFile ? (
                    <img
                      src={URL.createObjectURL(documentFile)}
                      onClick={() => openPreview(documentFile, "Document Preview")}
                      alt="Uploaded document"
                      style={{ width: 150, height: "auto", borderRadius: 6, objectFit: "cover", cursor: "pointer" }}
                    />
                  ) : (
                    <div style={{ width: 150, height: 100, background: "#e5e7eb", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      No document
                    </div>
                  )}

                  {/* ── Document number: masked or full ── */}
                  <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: 1, fontFamily: "monospace" }}>
                    {settingsLoading
                      ? "..."
                      : formatDocNumber(documentNumber, docType, showDocMasked)}
                  </div>
                </div>
              </div>
            </div>

            {/* Profile + Other details */}
            <div
              style={{
                background: "#f3f4f6",
                borderRadius: 12,
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {/* Profile + Name */}
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                {profileImage ? (
                  <img
                    src={URL.createObjectURL(profileImage)}
                    alt="Profile"
                    onClick={() => openPreview(profileImage, "Profile Photo")}
                    style={{ width: 60, height: 60, borderRadius: "50%", objectFit: "cover", cursor: "pointer" }}
                  />
                ) : (
                  <div
                    style={{
                      width: 60, height: 60, borderRadius: "50%", background: "#f9fafb",
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
                    }}
                  >
                    No Photo
                  </div>
                )}
                <span style={{ fontSize: 15, fontWeight: 600 }}>{name}</span>
              </div>

              {/* Details */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", fontSize: 13 }}>
                  <span style={{ width: 110, color: "#6b7280" }}>Gender</span>
                  <span style={{ width: 15 }}>:</span>
                  <span>{gender || "-"}</span>
                </div>
                <div style={{ display: "flex", fontSize: 13 }}>
                  <span style={{ width: 110, color: "#6b7280" }}>Vehicle Type</span>
                  <span style={{ width: 15 }}>:</span>
                  <span>{vehicleType || "-"}</span>
                </div>
                <div style={{ display: "flex", fontSize: 13 }}>
                  <span style={{ width: 110, color: "#6b7280" }}>Vehicle Number</span>
                  <span style={{ width: 15 }}>:</span>
                  <span>{vehicleNumber || "-"}</span>
                </div>
              </div>
            </div>

            {/* Assigned Flats */}
            <div style={{ marginBottom: 24, marginTop: 18 }}>
              <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 8 }}>
                Assigned Flats
              </label>
              <div
                style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "12px 14px", border: "1px solid #d1d5db",
                  borderRadius: 8, background: "#f9fafb", minHeight: 48,
                }}
              >
                <img src={BuildingIcon} alt="building" style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0 }} />
                <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {selectedFlats && selectedFlats.length > 0 ? (
                    selectedFlats.map((flat, index) => (
                      <div
                        key={index}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          padding: "6px 12px", background: "#ffffff",
                          border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13, fontWeight: 500,
                        }}
                      >
                        <span style={{ color: "#6b7280" }}>
                          {flat.towerName || flat.towerNo || `Tower ${flat.towerId || ""}`}
                        </span>
                        <span style={{ color: "#d1d5db" }}>•</span>
                        <span style={{ color: "#374151" }}>
                          {flat.flatNo || flat.flatName || `Flat ${flat.flatId || ""}`}
                        </span>
                      </div>
                    ))
                  ) : flats ? (
                    <span style={{ color: "#374151", fontSize: 14 }}>{flats}</span>
                  ) : (
                    <span style={{ color: "#9ca3af", fontSize: 14 }}>No flats assigned</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 pb-4 d-flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="btn flex-grow-1 text-white"
              style={{
                background: loading ? "#a0c4ff" : "#1C80D2",
                borderRadius: 30,
                padding: "14px",
                fontWeight: 600,
              }}
            >
              {loading ? "Saving..." : `Add ${roleName}`}
            </button>
          </div>
        </div>
      </div>

      {/* Digital Pass Modal */}
      {createdStaff && (
        <DigitalPassGenerator
          show={showPassGenerator}
          onHide={() => {
            setShowPassGenerator(false);
            setCreatedStaff(null);
            onSuccess?.();
            onHide();
          }}
          staffData={createdStaff}
        />
      )}

      <ImagePreviewModal
        show={showModal}
        imageUrl={previewUrl}
        onClose={handleClosePreview}
        title={previewTitle}
      />
    </>
  );
};

export default ServiceStaffModal;